import { hexToRgb, getLuminance, safe } from '../utils/contrast.js';
import { atributoDeBoton } from '../utils/boton-estilo.js';
import { t } from '../utils/i18n.js';
// Escape SSOT (utils/dom.js), el mismo que usan header.js/footer.js. Aquí hace falta porque
// props como `recipient`, `placeholder` o las `options` del select los escribe el CLIENTE
// (extractor) o el LLM al clonar una web ajena, y viajan a ATRIBUTOS y a texto plano: sin
// escapar, un `"` cierra el atributo y lo que siga es markup ejecutable dentro del editor
// (el saneo XSS del servidor solo cubre el HTML ya publicado, no el canvas de edición).
// Los campos contenteditable (title/subtitle/label/submit_label/success_msg) NO se escapan a
// propósito: el extractor los lee con innerHTML —son texto RICO— y escaparlos los rompería en
// cada ciclo de edición (&amp; → &amp;amp; …). `recipient` sí se escapa porque el extractor lo
// lee con textContent (core/extractor.js), así que el viaje de vuelta es texto plano.
import { escapeHtml } from '../utils/dom.js';

/**
 * FORM — Formulario de captación de leads / contacto.
 *
 * Renderiza un <form> REAL con inputs/textarea/select nativos para que los
 * visitantes del sitio publicado escriban (NO son contenteditable). Lo editable
 * en el builder son las ETIQUETAS (label), el título/subtítulo, el texto del
 * botón y el mensaje de éxito — todos vía data-field, igual que el resto de bloques.
 *
 * El ENVÍO real (validación servidor, email/WhatsApp, guardado del Lead) lo
 * cablea un handler GLOBAL externo (animations/form-submit.js) que lee el
 * <form class="ai-form">: el destino viaja en data-recipient-type / data-recipient,
 * y cada control lleva data-field-name para identificarse. Este bloque NO gestiona
 * el submit. Todos los campos (incluidos los nuevos date/select/number de reservas)
 * se envían a /site_lead/ y se guardan en Lead.data por su etiqueta → sin backend nuevo.
 *
 * Modelo de datos (props):
 *   { title, subtitle,
 *     fields: [{ label, type:'text'|'email'|'tel'|'textarea'|'date'|'number'|'select',
 *                placeholder, required, options?:[...], min?, max?, step? }],
 *     submit_label, success_msg,
 *     recipient_type:'email'|'whatsapp', recipient }
 *
 * Las fields[] NO se editan inline (son inputs vivos); el extractor solo lee la
 * label editable de cada una y PRESERVA type/placeholder/required/options (ver
 * core/extractor.js → CUSTOM_HANDLERS.form). Reordenar/añadir/borrar campos
 * reutiliza los handlers globales (data-array="fields").
 *
 * Variantes (CSS-driven, contenido compartido → DRY):
 *   v1 = Centrado: título arriba + formulario estrecho centrado.
 *   v2 = Split: texto/título a la izquierda + formulario a la derecha.
 *   v3 = Banda newsletter: franja compacta título+bajada a un lado e input+botón
 *        en línea al otro (para captar suscriptores de email).
 *
 * renderFormLike() es el render REUTILIZABLE: el bloque 'reservas' (blocks/reservas.js)
 * lo llama con kind:'reservas' para no duplicar toda esta maquinaria (DRY). El markup
 * (.ai-form/.form-field) es idéntico → el mismo extractor, el mismo form-submit.js y el
 * mismo CSS sirven a ambos. Lo que SÍ ramifica por `kind` es el id de la sección y el
 * conjunto de VARIANTES permitidas (ver el clamp más abajo): `form` tiene banda newsletter
 * (v3) y `reservas` no, así que una reserva con v3 dejaba de ser un formulario de cita.
 *
 * OJO: esto NO es el sitio para meter un flujo con estado. Reservar de verdad (huecos reales,
 * calendario, confirmación) vive en su propio bloque, `blocks/reserva-live.js`.
 */

// Slug seguro para name/id de cada campo (sin acentos, kebab-case).
const slugify = (s) => (s || '').toString().toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

// autocomplete nativo por tipo → mejor UX/accesibilidad en el sitio publicado.
const AUTOCOMPLETE = { email: 'email', tel: 'tel' };

// Tipos de <input> nativos soportados (el resto cae a 'text'). date/number amplían
// el form para reservas (fecha, nº de personas); select se maneja aparte.
const INPUT_TYPES = ['text', 'email', 'tel', 'date', 'number'];

/**
 * Render REUTILIZABLE del bloque form (y sus clones, p.ej. reservas).
 * @param {object} rawProps  props del bloque
 * @param {number} index     índice del bloque en la página
 * @param {object} [opts]     { kind } — 'form' (default) | 'reservas' | ...
 */
export const renderFormLike = (rawProps, index = 0, opts = {}) => {
    const kind = (opts.kind || 'form').toString();
    const p = safe(rawProps);
    // La variante se CLAVA a las que el kind declara en block-meta.js. `form` tiene v1/v2/v3;
    // `reservas`, solo v1/v2 → una `reservas` con variant:'v3' (JSON viejo, edición a mano, o
    // un block_meta.json desincronizado porque faltó `npm run build`) se pintaba como BANDA DE
    // NEWSLETTER: un formulario de cita convertido en captación de emails, sin sus campos.
    // El clamp vive aquí, en el render compartido, para que cualquier kind futuro lo herede.
    const VARIANTES = { form: ['v1', 'v2', 'v3'], reservas: ['v1', 'v2'] };
    const permitidas = VARIANTES[kind] || VARIANTES.form;
    const variant = permitidas.indexOf(p.variant) !== -1 ? p.variant : permitidas[0];

    // safe() NO normaliza p.fields → lo defaulteamos aquí.
    const fields = Array.isArray(p.fields) ? p.fields : [];
    const title = p.title || 'Hablemos';
    const subtitle = p.subtitle || 'Déjanos tus datos y te respondemos en menos de 24 horas.';
    const submitLabel = p.submit_label || 'Enviar mensaje';
    const successMsg = p.success_msg || '¡Gracias! Hemos recibido tu mensaje y te contactaremos pronto.';
    const recipientType = p.recipient_type === 'whatsapp' ? 'whatsapp' : 'email';
    const recipient = p.recipient || '';

    const isDark = getLuminance(...hexToRgb(p.bgColor)) < 0.179;
    const btnBg = isDark ? '#ffffff' : '#111827';
    const btnIcon = isDark ? '#111827' : '#ffffff';

    // Toolbar por campo (reusa handlers globales: add/borrar/drag, keyed por data-array/data-index).
    const itemToolbar = (i) => `
        ${i !== 0 ? `<button class="item-add-btn card-add-top" data-array="fields" data-index="${i}">+</button>` : ''}
        <button class="item-add-btn card-add-bottom" data-array="fields" data-index="${i}">+</button>
        <div class="item-tools">
            <button class="tool-btn item-drag-handle" title="Mover" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.drag}</button>
            <button class="tool-btn item-delete-btn" data-array="fields" data-index="${i}" title="Borrar" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.trash}</button>
        </div>`;

    // --- Un campo: label EDITABLE (data-field="label") + control REAL (no editable) ---
    const renderField = (field, i) => {
        const rawType = (field.type || 'text').toString().toLowerCase();
        const isTextarea = rawType === 'textarea';
        const isSelect = rawType === 'select';
        const inputType = INPUT_TYPES.includes(rawType) ? rawType : 'text';
        const required = !!field.required;
        const label = field.label || '';
        const placeholder = field.placeholder || '';
        // name estable: explícito > slug del label > posicional. Permite identificar el dato.
        const fname = slugify(field.name) || slugify(label) || `campo-${i + 1}`;
        const fid = `form-${index}-f${i}`;
        const reqAttr = required ? ' required aria-required="true"' : '';
        const auto = AUTOCOMPLETE[inputType] ? ` autocomplete="${AUTOCOMPLETE[inputType]}"` : '';

        let control;
        if (isTextarea) {
            control = `<textarea id="${fid}" name="${fname}" data-field-name="${fname}" class="form-input form-textarea" rows="4" placeholder="${escapeHtml(placeholder)}"${reqAttr}></textarea>`;
        } else if (isSelect) {
            // Desplegable (p.ej. franja horaria de una reserva). Nativo → tiene .value,
            // así form-submit.js lo recoge y /site_lead/ lo guarda por su etiqueta.
            const options = Array.isArray(field.options) ? field.options : [];
            const optsHtml = options.map((o) => {
                const v = (o == null ? '' : o).toString();
                return `<option value="${escapeHtml(v)}">${escapeHtml(v)}</option>`;
            }).join('');
            const ph = placeholder || label || t('form_select');
            control = `<select id="${fid}" name="${fname}" data-field-name="${fname}" class="form-input form-select"${reqAttr}>
                <option value="" disabled selected hidden>${escapeHtml(ph)}</option>
                ${optsHtml}
            </select>`;
        } else {
            // Límites opcionales para number/date (p.ej. mínimo de personas, fecha mínima).
            const minAttr = field.min != null && field.min !== '' ? ` min="${escapeHtml(field.min)}"` : '';
            const maxAttr = (inputType === 'number' && field.max != null && field.max !== '') ? ` max="${escapeHtml(field.max)}"` : '';
            const stepAttr = (inputType === 'number' && field.step != null && field.step !== '') ? ` step="${escapeHtml(field.step)}"` : '';
            control = `<input id="${fid}" name="${fname}" data-field-name="${fname}" type="${inputType}" class="form-input" placeholder="${escapeHtml(placeholder)}"${reqAttr}${auto}${minAttr}${maxAttr}${stepAttr}>`;
        }

        return `
        <div class="form-field${isTextarea ? ' form-field-wide' : ''} sortable-item stagger-item" data-item-index="${i}" data-array-name="fields" data-array="fields" data-index="${i}" style="--stagger-i:${i};">
            ${itemToolbar(i)}
            <div class="form-label-row">
                <label class="form-label" for="${fid}" data-field="label" contenteditable="true">${label}</label>
                ${required ? `<span class="form-req" title="${t('form_required')}" aria-hidden="true">*</span>` : ''}
            </div>
            ${control}
        </div>`;
    };

    const fieldsHtml = fields.map(renderField).join('');

    // ── CHROME DEL EDITOR (.editor-only → compileSiteHtml lo elimina) ──────────────────
    // Dos cosas que el cliente no podía saber y le costaban leads:
    //   1. Dentro del canvas el envío es INERTE a propósito (animations/form-submit.js) —
    //      probarlo aquí y no ver nada parecía una avería.
    //   2. A dónde van los mensajes vivía SOLO en `props.recipient`, invisible y sin
    //      ninguna forma de editarlo.
    // Se pinta como texto EDITABLE (`data-field="recipient"`, lo persiste el handler `form`
    // de core/extractor.js). VACÍO ES EL ESTADO NORMAL, no un descuido: el servidor cae al
    // email del ERP del tenant (views_sites.site_lead → legal_pages.email_de_contacto) y el
    // aviso llega igual. El backend dejó de SEMBRAR ese email en los props a propósito —lo
    // publicaba en el `data-recipient` del HTML público, a la vista de cualquier scraper de
    // spam— así que el rótulo tiene que decir qué pasa cuando está vacío.
    // Los literales son chrome del EDITOR (como el resto de paneles) → van en español fijo:
    // no son contenido del sitio y nunca se publican.
    const destSvg = `<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>`;
    const editorNote = `
        <div class="form-editor-note editor-only">
            <span class="form-editor-note__msg">${destSvg} Los envíos solo funcionan en el sitio publicado.</span>
            <span class="form-editor-note__dest">
                ${recipientType === 'whatsapp' ? 'Avisar por WhatsApp al:' : 'Avisar por email a:'}
                <span class="form-dest-value" data-field="recipient" contenteditable="true"
                      data-placeholder="Se avisará al email de tu ERP (edítalo para cambiarlo)">${escapeHtml(recipient)}</span>
            </span>
        </div>`;

    // Honeypot anti-spam (oculto por CSS): el handler global descarta si llega relleno.
    const honeypot = `
        <div class="form-hp" aria-hidden="true">
            <label>No rellenar<input type="text" name="_hp" tabindex="-1" autocomplete="off"></label>
        </div>`;

    // Mensaje de éxito: editable en el builder, oculto en el sitio hasta que el
    // handler global le añade la clase .is-visible tras un envío correcto.
    const successHtml = `
        <div class="form-success" data-field="success_msg" contenteditable="true" role="status" aria-live="polite">${successMsg}</div>`;

    // <form> con metadatos de destino para el handler global de envío.
    const formHtml = (extraClass = '') => `
        <form class="ai-form ${extraClass}" data-form-index="${index}" data-recipient-type="${recipientType}" data-recipient="${escapeHtml(recipient)}">
            <div class="form-grid">
                ${fieldsHtml}
            </div>
            <div class="form-actions">
                <button type="submit" class="form-submit btn-action" data-field="submit_label" contenteditable="true"${atributoDeBoton(p, 'submit_label')}>${submitLabel}</button>
            </div>
            ${honeypot}
            ${successHtml}
            ${editorNote}
        </form>`;

    const headHtml = `
        <h2 data-field="title" contenteditable="true" class="form-title">${title}</h2>
        <p data-field="subtitle" contenteditable="true" class="form-subtitle">${subtitle}</p>`;

    const sectionId = `bloque-${kind}-${index}`;
    // La banda newsletter (v3) es más baja que el formulario a página completa.
    const pad = variant === 'v3' ? '56px 8%' : '100px 8%';
    const baseStyle = `--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --accent-contrast:${p.accentContrast}; --font:'${p.fontFamily || "Inter"}'; padding: ${pad};`;

    // ═══════════════════════════════════════════
    // v3 — Banda newsletter: franja compacta texto | input+botón en línea
    // (SOLO kind 'form': ver el clamp de variante arriba)
    // ═══════════════════════════════════════════
    if (variant === 'v3') {
        return `
    <section id="${sectionId}" class="reveal variant-${variant} newsletter-band" style="${baseStyle}">
        <div class="nl-band">
            <div class="nl-band-text">
                ${headHtml}
            </div>
            <div class="nl-band-form">
                ${formHtml('ai-form-band')}
            </div>
        </div>
    </section>`;
    }

    // ═══════════════════════════════════════════
    // v2 — Split: texto izquierda + formulario derecha
    // ═══════════════════════════════════════════
    if (variant === 'v2') {
        return `
    <section id="${sectionId}" class="reveal variant-${variant}" style="${baseStyle}">
        <div class="form-split">
            <div class="form-split-text">
                ${headHtml}
            </div>
            <div class="form-split-form">
                ${formHtml()}
            </div>
        </div>
    </section>`;
    }

    // ═══════════════════════════════════════════
    // v1 — Centrado: título arriba + formulario estrecho centrado
    // ═══════════════════════════════════════════
    return `
    <section id="${sectionId}" class="reveal variant-${variant}" style="${baseStyle}">
        <div class="form-head form-head-center">
            ${headHtml}
        </div>
        ${formHtml('ai-form-center')}
    </section>`;
};

export const formBlock = (rawProps, index = 0) => renderFormLike(rawProps, index, { kind: 'form' });
